import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../auth/contexts/AuthContext';
import {
  listPartnerPhoneCalls, MAX_PHONE_CALL_TRANSCRIPT_LENGTH, newPhoneCallId,
  savePartnerPhoneCall, type PhoneCallPage, type PhoneCallPerson,
} from '../services/partnerPhoneCalls';
import '../styles/PhoneCallDialog.css';

export default function PhoneCallButton({ partnerId, person }: { partnerId: string; person: PhoneCallPerson }): React.ReactElement {
  const [mode, setMode] = useState<'add' | 'history' | null>(null);
  return (
    <div className="partner-phone-call-actions">
      <button type="button" onClick={() => setMode('add')}>Add a phone call</button>
      <button type="button" onClick={() => setMode('history')}>View calls</button>
      {mode && <PhoneCallDialog key={`${partnerId}:${person.type}:${person.id}`} partnerId={partnerId} person={person} mode={mode} onClose={() => setMode(null)} />}
    </div>
  );
}

function PhoneCallDialog({ partnerId, person, mode, onClose }: {
  partnerId: string; person: PhoneCallPerson; mode: 'add' | 'history'; onClose: () => void;
}): React.ReactElement {
  const { currentUser } = useAuth();
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [callId, setCallId] = useState(() => newPhoneCallId(partnerId));
  const [transcript, setTranscript] = useState('');
  const [callDate, setCallDate] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [historyVisible, setHistoryVisible] = useState(mode === 'history');
  const [history, setHistory] = useState<PhoneCallPage>({ calls: [], cursor: null });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyRevision, setHistoryRevision] = useState(0);

  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    return () => element?.close();
  }, []);

  useEffect(() => {
    if (!historyVisible) return;
    let active = true;
    setHistoryLoading(true);
    setHistoryError('');
    listPartnerPhoneCalls(partnerId, person)
      .then((page) => { if (active) setHistory(page); })
      .catch((err) => { if (active) setHistoryError(err instanceof Error ? err.message : 'Could not load calls.'); })
      .finally(() => { if (active) setHistoryLoading(false); });
    return () => { active = false; };
    // Names are snapshots; only identity changes should reload history.
  }, [partnerId, person.id, person.type, historyVisible, historyRevision]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => {
    if (savingRef.current) return;
    if ((transcript || callDate) && !window.confirm('Discard this unsaved phone call?')) return;
    onClose();
  };

  const loadMore = async () => {
    if (!history.cursor || historyLoading) return;
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const page = await listPartnerPhoneCalls(partnerId, person, history.cursor);
      setHistory((previous) => ({ calls: [...previous.calls, ...page.calls], cursor: page.cursor }));
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : 'Could not load more calls.');
    } finally {
      setHistoryLoading(false);
    }
  };

  return createPortal(
    <dialog ref={dialog} className="partner-phone-call-dialog" aria-labelledby={titleId} onCancel={(event) => { event.preventDefault(); close(); }}>
      <div className="partner-phone-call-dialog__header">
        <div><h2 id={titleId}>{mode === 'add' ? 'Add a phone call' : 'Phone calls'}</h2><p>{person.displayName || person.id} · {person.type === 'parent' ? 'Parent' : 'Partner contact'}</p></div>
        <button type="button" aria-label="Close phone calls" onClick={close} disabled={saving}>×</button>
      </div>
      {mode === 'add' && <form onSubmit={async (event) => {
        event.preventDefault();
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        setError('');
        setSuccess(false);
        try {
          await savePartnerPhoneCall({ id: callId, partnerId, person, transcript, callDate, actorId: currentUser?.uid || '' });
          setTranscript('');
          setCallDate('');
          setSuccess(true);
          setCallId(newPhoneCallId(partnerId));
          setHistoryVisible(true);
          setHistoryRevision((value) => value + 1);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Could not save the phone call. Your transcript is still here; try again.');
        } finally {
          savingRef.current = false;
          setSaving(false);
        }
      }}>
        <label>Call date (optional)<input type="date" value={callDate} disabled={saving} onChange={(event) => setCallDate(event.target.value)} /></label>
        <label>Phone call transcript<textarea autoFocus rows={12} value={transcript} disabled={saving} placeholder="Paste the full transcript here, including speaker names and timestamps if available." onChange={(event) => { setTranscript(event.target.value); setSuccess(false); }} /></label>
        <p className="partner-phone-call-dialog__hint">Saved exactly as pasted and linked to this person. {transcript.length.toLocaleString()} / {MAX_PHONE_CALL_TRANSCRIPT_LENGTH.toLocaleString()} characters.</p>
        {error && <p role="alert" className="partner-phone-call-dialog__error">{error}</p>}
        {transcript.length > MAX_PHONE_CALL_TRANSCRIPT_LENGTH && <p role="alert" className="partner-phone-call-dialog__error">Transcript is too long to save. Nothing has been shortened or saved.</p>}
        {success && <p role="status">Phone call saved.</p>}
        <div className="partner-phone-call-dialog__actions"><button type="button" onClick={close} disabled={saving}>{success ? 'Done' : 'Cancel'}</button><button type="submit" className="partner-primary-button" disabled={saving || !transcript.trim() || transcript.length > MAX_PHONE_CALL_TRANSCRIPT_LENGTH}>{saving ? 'Saving…' : 'Save phone call'}</button></div>
      </form>}
      {!historyVisible && <button type="button" onClick={() => setHistoryVisible(true)}>View saved calls</button>}
      {historyVisible && <section aria-label="Saved phone calls">
        <h3>Saved calls</h3>
        {historyError && <p role="alert" className="partner-phone-call-dialog__error">{historyError} <button type="button" onClick={() => setHistoryRevision((value) => value + 1)}>Retry</button></p>}
        {!historyLoading && !historyError && history.calls.length === 0 && <p>No phone calls saved for this person yet.</p>}
        {history.calls.map((call) => {
          const savedAt = call.createdAt instanceof Date ? call.createdAt : call.createdAt?.toDate();
          return <details key={call.id} className="partner-phone-call-dialog__call">
            <summary>{call.callDate ? `Call on ${call.callDate}` : 'Call date not recorded'}<span>Added {savedAt ? savedAt.toLocaleString() : 'just now'}</span></summary>
            <pre>{call.transcript}</pre>
          </details>;
        })}
        {historyLoading && <p role="status">Loading calls…</p>}
        {history.cursor && <button type="button" disabled={historyLoading} onClick={() => void loadMore()}>Load older calls</button>}
      </section>}
    </dialog>, document.body,
  );
}
